import { ApiGatewayMutations } from "@/api/apiGateway/mutations"
import {
  BackofficeRestMutations,
  SaveSmartMeterInterestPayload,
} from "@/api/backofficeRest/mutations"
import { MutationResult } from "@/api/utils/types"
import { useMutation } from "@/api/utils/useQuery"
import {
  IImbpReserveSlot,
  IImbpReserveSlotSeralized,
  IImbpSubmit,
  IImbpSubmitSeralized,
  IImbpRegisterSmartMeterInterest,
} from "@/mutators/imbp/ImbpMutator.types"
import { AuthService } from "@/services/AuthService"
import dayjs from "dayjs"

export interface IImbpMutator {
  submit(
    onSuccessFn: (payload: unknown) => unknown,
    onErrorFn: (payload: unknown) => unknown,
  ): MutationResult<IImbpSubmit>
  reserveSlot(
    onSuccessFn: (payload: unknown) => unknown,
  ): MutationResult<IImbpReserveSlot>
  cancelBookings(
    id: number,
    bookingId: string,
    mop?: string,
  ): MutationResult<{ reason: string; initiatedBy: string }>
  submitSmartMeterInterest(): MutationResult<IImbpRegisterSmartMeterInterest>
  removeSmartMeterInterest(): MutationResult<string>
}

class ImbpMutatorClass implements IImbpMutator {
  reserveSlot(onSuccessFn) {
    return useMutation({
      ...ApiGatewayMutations.imbp.reserveSlot(),
      serializerFn: (data: IImbpReserveSlot): IImbpReserveSlotSeralized => {
        return {
          postcode: data.imbpAccount.formattedPostcode,
          timeSlot:
            (data.mop === "SMS" &&
              data.appointmentData.appointmentSlot.timeSlot) ||
            data.appointmentData.appointmentSlot.timeRange,
          day: dayjs(data.appointmentData.appointmentSlot.date).format(
            "YYYY-MM-DD",
          ),
          meterType: data.jobType.selectedMeterTypeAes,
          appointmentType: data.jobType.selectedAppointmentType,
          installType: data.jobType.selectedInstallationType,
          ...(!!data.mop && { mop: data.mop }),
        }
      },
      onSuccessFn,
    })
  }

  cancelBookings(id: number, bookingId: string, mop?: string) {
    return useMutation({
      ...ApiGatewayMutations.imbp.cancelBookings(id, bookingId),
      serializerFn: (data: { reason: string; initiatedBy: string }) => ({
        ...data,
        ...(!!mop && { mop }),
      }),
    })
  }

  submit(onSuccessFn, onErrorFn): MutationResult<IImbpSubmit> {
    return useMutation({
      ...ApiGatewayMutations.imbp.submit(),
      serializerFn: (data: IImbpSubmit): IImbpSubmitSeralized => {
        return {
          "customer-first-name":
            data.installationEssentials.contactFirstName ||
            data.imbpAccount.contactInfo?.firstName ||
            "",
          "customer-last-name":
            data.installationEssentials.contactLastName ||
            data.imbpAccount.contactInfo?.lastName ||
            "",
          "customer-email":
            data.installationEssentials.contactEmail ||
            data.imbpAccount.contactInfo?.email ||
            "",
          "customer-phone":
            data.installationEssentials.contactPhoneNumber ||
            data.imbpAccount.contactInfo?.phone1 ||
            data.imbpAccount.contactInfo?.phone2 ||
            "",
          "customer-city": data.imbpAccount.address?.addressLine3 || "Unknown",
          "customer-postcode": data.imbpAccount.formattedPostcode,
          "customer-address": data.imbpAccount.customerAddress,
          "account-id": data.imbpAccount?.billingAccountId?.toString() ?? "",
          mpan: data.smartMeterEligibility.eligibleForElectricExchange
            ? (data.imbpAccount.mpan ?? "")
            : "",
          mprn: data.smartMeterEligibility.eligibleForGasExchange
            ? (data.imbpAccount.mprn ?? "")
            : "",
          "gas-meter-serial-number": data.smartMeterEligibility
            .eligibleForGasExchange
            ? (data.imbpAccount.gasMeterSerialNumber ?? "")
            : "",
          "electric-meter-serial-number": data.smartMeterEligibility
            .eligibleForElectricExchange
            ? (data.imbpAccount?.electricityMeterSerialNumber ?? "")
            : "",
          "reservation-id": data.reservationId,
          "agent-email":
            AuthService.getCurrentUser()?.username?.toString() ?? "",
          "meter-reading-frequency": data.meterReadingFrequency,
          questionnaire: data.questionnaire,
        }
      },
      onSuccessFn,
      onErrorFn,
    })
  }

  submitSmartMeterInterest() {
    return useMutation({
      ...BackofficeRestMutations.smartMeter.saveSmartMeterInterest(),
      serializerFn: (
        data: IImbpRegisterSmartMeterInterest,
      ): SaveSmartMeterInterestPayload => {
        return {
          billingAccountId: data.billingAccountId,
          body: {
            comment: data.customerInterest.comment ?? "",
            postcode: data.postcode,
            source: "IMBP",
            interest: data.customerInterest.interest,
          },
        }
      },
    })
  }

  removeSmartMeterInterest(): MutationResult<string> {
    return useMutation({
      ...BackofficeRestMutations.smartMeter.removeSmartMeterInterest(),
      serializerFn: (billingAccountId: string) => billingAccountId,
    })
  }
}

export const ImbpMutator = new ImbpMutatorClass()
